// WI-433: shared option lists for the Group and School add/edit forms.

// Countries pinned to the top of the searchable country select.
export const PINNED_COUNTRIES = [
  'England',
  'Northern Ireland',
  'Scotland',
  'United Arab Emirates',
  'United States',
  'Qatar',
];

// The remainder of the country list (alphabetical). Kept deliberately broad; the
// select is searchable so length is not a usability concern.
const OTHER_COUNTRIES = [
  'Wales',
  'Ireland',
  'Australia',
  'Austria',
  'Bahrain',
  'Belgium',
  'Brazil',
  'Canada',
  'China',
  'Cyprus',
  'Denmark',
  'Egypt',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hong Kong',
  'India',
  'Indonesia',
  'Italy',
  'Japan',
  'Kenya',
  'Kuwait',
  'Luxembourg',
  'Malaysia',
  'Malta',
  'Mexico',
  'Netherlands',
  'New Zealand',
  'Nigeria',
  'Norway',
  'Oman',
  'Pakistan',
  'Poland',
  'Portugal',
  'Saudi Arabia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Spain',
  'Sweden',
  'Switzerland',
  'Thailand',
  'Turkey',
  'Vietnam',
].sort((a, b) => a.localeCompare(b));

// Pinned first (in their declared order), then the rest alphabetically.
export const COUNTRY_OPTIONS = [...PINNED_COUNTRIES, ...OTHER_COUNTRIES];

// WI-433: Group Type dropdown values.
export const GROUP_TYPE_OPTIONS = [
  'MAT',
  'International School Group',
  'UK Independent School Group',
  'Other',
];

// The Group GUID is only relevant to UK MATs.
export const GROUP_TYPE_MAT = 'MAT';

// Single source of truth for the Group GUID gate, shared by the add and edit forms so the
// field (and its GIAS MAT-lookup trigger) only ever appears for MATs.
export const shouldShowGroupGuid = (groupType) => groupType === GROUP_TYPE_MAT;

// WI-433: School Type dropdown values shown in the UI. The backend still accepts any
// GIAS value, so this list constrains the UI only.
export const SCHOOL_TYPE_OPTIONS = [
  'UK State (Part of a MAT)',
  'UK State (LA)',
  'UK Special',
  'UK Independent',
  'International Independent',
];

// WI-433: the BigQuery data-storage location LIST is owned by the backend and fetched via
// lookupService.getDataStorageLocations() — the backend validates inbound values against the
// same list, so an invalid region can't break dataset provisioning. Only the default is kept
// here as a fallback for the initial render / if the lookup call fails.
export const DEFAULT_DATA_STORAGE_LOCATION = 'europe-west2';

// Curated-but-open list for the school contact's Role. Backs a freeSolo Autocomplete so
// operators pick a common title quickly while GIAS-supplied or bespoke titles still go
// through as free text; the stored value is always a plain string.
export const CONTACT_ROLE_OPTIONS = [
  'Headteacher',
  'Executive Headteacher',
  'Deputy Headteacher',
  'Assistant Headteacher',
  'Head of School',
  'Principal',
  'School Business Manager',
  'School Administrator',
  'Office Manager',
  'HR Manager',
  'CEO',
  'CFO',
  'Chair of Governors',
  'Other',
];

// Boarding / Post 16 / Nursery are now true/false. Stored as booleans; this list drives
// the Yes/No selects (a third "unset" option keeps an unknown value unset).
export const YES_NO_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

// GIAS provenance maps the Boarders / OfficialSixthForm fields as numeric codes:
//   1 = Not applicable, 2 = No, 3 = Yes, 9 = Not recorded.
// Only an explicit Yes/No is meaningful; everything else leaves the toggle unset (null)
// so we never invent a false where GIAS simply has no answer. This mirrors the backend
// GiasFieldMapper (3/Yes -> true, 2/No -> false, anything else -> null).
export const giasCodeToBoolean = (code) => {
  if (code === null || code === undefined || code === '') return null;
  const normalised = String(code).trim();
  if (normalised === '3') return true;
  if (normalised === '2') return false;
  return null;
};

// Nursery arrives as a descriptive string (e.g. "Has Nursery Classes" / "No Nursery Classes").
// Interpret the presence/absence of nursery provision; an unrecognised value stays unset.
export const giasNurseryToBoolean = (description) => {
  if (!description) return null;
  const text = String(description).trim().toLowerCase();
  if (text === '' || text.includes('not recorded') || text.includes('not applicable')) return null;
  if (text.startsWith('no ') || text === 'no') return false;
  if (text.includes('nursery') || text === 'yes') return true;
  return null;
};
